import { Box, Group, Stack, Text } from '@mantine/core'
import type { ReactNode } from 'react'
import type { BoggleGameModeDefinition } from '~/boggle/gameModes'
import { GameModePill, RouteHeaderLabel } from '~/boggle/ui/atoms/Labels'
import { BackButton } from '~/boggle/ui/atoms/Buttons'

/** Bottom inset (px) for run-surface controls anchored to the shell (no footer chrome). */
export const BOGGLE_RUN_SEED_BOTTOM_INSET_PX = 12

export function GameRouteBody({ children }: { children: ReactNode }) {
  return (
    <Stack flex={1} mih={0}>
      {children}
    </Stack>
  )
}

/**
 * Game mode label under “Patter”, styled like catalog mode pills (`GameModePill`).
 */
export function GameModeHeaderPill({ gameMode }: { gameMode: BoggleGameModeDefinition }) {
  return (
    <Box pl={6} pos="relative">
      <GameModePill
        color={gameMode.color}
        description={gameMode.tagline}
        name={gameMode.name}
        withInfoIcon={false}
      />
    </Box>
  )
}

/**
 * Primary page header chrome for Patter public routes — single centered row.
 */
export function GamePageHeader({
  content,
  visibilityHidden = false,
}: {
  content?: ReactNode | null
  /** Keeps occupied space for stable layout while hiding chrome visually. */
  visibilityHidden?: boolean
}) {
  return (
    <Box
      aria-hidden={visibilityHidden ? true : undefined}
      component="header"
      mih={56}
      pos="relative"
      pt={4}
      px={4}
      pl={8}
      style={
        visibilityHidden
          ? { visibility: 'hidden', pointerEvents: 'none' }
          : undefined
      }
    >
      <Group align="center" gap={8} justify="center" miw={0} p={4} pt={0} wrap="nowrap" w="100%">
        {content}
      </Group>
    </Box>
  )
}

export function StandardBestScoreHeadingRow({
  bestScore,
  headerTrailing = false,
}: {
  bestScore: number | null
  /** When true, right-align this row inside its layout cell (e.g. non-header surfaces). */
  headerTrailing?: boolean
}) {
  return (
    <Box pos="relative">
      <Group gap={6} justify={headerTrailing ? 'flex-end' : 'flex-start'} wrap="nowrap">
        <Text c="#d8ccb6" ff="Tilt Neon, sans-serif" fz={20} fw={400} lh={1}>
          Best Score:
        </Text>
        <Text
          c="white"
          ff="Tilt Neon, sans-serif"
          fz={20}
          fw={600}
          lh={1}
          lts={1}
        >
          {bestScore ?? '-'}
        </Text>
      </Group>
    </Box>
  )
}

export function HeaderBackLink({
  label = 'Back',
  onClick,
  trailingLayout = false,
}: {
  label?: string
  onClick: () => void
  /** When true, styles match the reveal-phase trailing control (aligned right). */
  trailingLayout?: boolean
}) {
  return (
    <BackButton
      c="rgba(242, 232, 217, 0.78)"
      iconSize={12}
      fz={16}
      px={20}
      py={20}
      onClick={onClick}
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: 5,
        lineHeight: 1,
        textAlign: trailingLayout ? 'right' : 'left',
        width: 'fit-content',
      }}
    >
      <Text fz={16} fw={500}>{label}</Text>
    </BackButton>
  )
}
